import {Subject} from 'rxjs';
import {BoInstanceToSave} from '../model/BoInstanceToSave';
import {DynamicFormConfig} from './DynamicFormConfig';
import {FormFieldInstance} from '../model/FormFieldInstance';
import {BoAutocompleteOption} from '../model/BoAutocompleteOption';
import {FieldControl} from './FieldControl';
import {BoInstanceAmountToSave} from '../model/BoInstanceAmountToSave';
import {BoiDialogData} from '../model/BoiDialogData';

export class DynamicFormControl {
  value: { [key: string]: string; } = {};

  private readonly valueChangedSubject = new Subject<{ fieldId: string, value: string }>();
  valueChanged = this.valueChangedSubject.asObservable();

  private readonly boInstanceSavedSubject = new Subject<BoInstanceToSave>();
  boInstanceSaved = this.boInstanceSavedSubject.asObservable();

  private readonly boInstanceAmountSavedSubject = new Subject<BoInstanceAmountToSave>();
  boInstanceAmountSaved = this.boInstanceAmountSavedSubject.asObservable();

  private readonly autocompleteClickedSubject = new Subject<BoAutocompleteOption>();
  autocompleteClicked = this.autocompleteClickedSubject.asObservable();

  private readonly boiDialogOpenEventSubject = new Subject<BoiDialogData>();
  boiDialogOpenEvent = this.boiDialogOpenEventSubject.asObservable();

  private readonly fieldsMap = new Map<string, FieldControl>();

  isValid = true;

  constructor(readonly config: DynamicFormConfig) {
    for (const field of config.fields) {
      const fieldControl = new FieldControl(field.value);
      // @ts-ignore
      field.__fieldControl = fieldControl;
      this.fieldsMap.set(field.fieldId, fieldControl);
    }
  }

  get fields(): FormFieldInstance[] {
    return this.config.fields;
  }

  setValue(fieldId: string, value: string) {
    const field = this.getOrElseThrow(fieldId);
    field.setValue(value);
  }

  saveBoInstance(toSave: BoInstanceToSave) {
    this.boInstanceSavedSubject.next(toSave);
  }

  changeValue(fieldId: string, value: string) {
    this.value[fieldId] = value;
    this.valueChangedSubject.next({fieldId, value});
  }

  closeAutocompleteFor(fieldId: string) {
    const field = this.getOrElseThrow(fieldId);
    field.closeAutoComplete();
  }

  showAutocompleteFor(fieldId: string, options: BoAutocompleteOption[]) {
    const field = this.getOrElseThrow(fieldId);
    field.showAutocompleteFor(options);
  }

  autocompleteClick(option: BoAutocompleteOption) {
    this.autocompleteClickedSubject.next(option);
  }

  reduceValidStatus() {
    this.isValid = true;
    for (const fieldControl of this.fieldsMap.values()) {
      this.isValid = this.isValid && fieldControl.isValid;
      if (!this.isValid) {
        return;
      }
    }
  }

  markAsTouched() {
    let notScrolled = true;
    for (const fieldControl of this.fieldsMap.values()) {
      fieldControl.touch();
      if (notScrolled && !fieldControl.isValid) {
        fieldControl.scrollToElement();
        notScrolled = false;
      }

    }
  }

  saveBoiAmount(amountToSave: BoInstanceAmountToSave) {
    this.boInstanceAmountSavedSubject.next(amountToSave);
  }

  openBoiDialog(data: BoiDialogData) {
    this.boiDialogOpenEventSubject.next(data);
  }

  completeValueChanged() {
    this.valueChangedSubject.complete();
  }

  private getOrElseThrow(fieldId: string): FieldControl {
    const field = this.fieldsMap.get(fieldId);

    if (!field) {
      throw new Error('Form field not found by id: ' + fieldId);
    }

    return field;
  }
}
